import * as XLSX from 'xlsx';
import { resolveOfferBreakdown } from './accountantOfferBreakdown';

const num = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const formatDate = (value?: string | null): string => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const resolveServiceStatus = (entry: any): string => {
  const candidate =
    entry?.lifecycleStatus ||
    entry?.lifecycle_status ||
    entry?.metadata?.lifecycleStatus ||
    entry?.metadata?.appointmentStatus ||
    entry?.metadata?.bookingStatus ||
    entry?.metadata?.orderStatus ||
    entry?.metadata?.serviceStatus ||
    null;
  return candidate ? String(candidate).toUpperCase() : '';
};

export type VendorSettlementExportVendor = {
  vendorId?: string;
  vendorName?: string | null;
  clinicName?: string | null;
  doctorName?: string | null;
  email?: string | null;
  phone?: string | null;
  vendorType?: string | null;
};

export type VendorSettlementExportSummary = {
  totalNetPayable?: number;
  pendingSettlement?: number;
  onHold?: number;
  settleableAmount?: number;
  settleableCount?: number;
  waitingT2Amount?: number;
  waitingT2Count?: number;
  totalCharges?: number;
  totalOfferDiscount?: number;
  totalPlatformCommission?: number;
  totalBaseCommission?: number;
  totalOfferAbsorbed?: number;
  payoutPolicyLabel?: string;
};

export type ExportVendorSettlementExcelInput = {
  vendor: VendorSettlementExportVendor | null | undefined;
  summary: VendorSettlementExportSummary;
  ledgerEntries: any[];
  settlementQueue: any[];
};

const slugify = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48) || 'vendor';

const mapLedgerRow = (entry: any, vendor: VendorSettlementExportVendor | null | undefined) => {
  const breakdown = resolveOfferBreakdown(entry);
  const isDeliveryPartner = String(vendor?.vendorType || '').toUpperCase() === 'DELIVERY_PARTNER';

  return {
    Date: formatDate(entry.serviceCompletedAt || entry.createdAt),
    'Service Ref': entry.serviceReferenceId || '',
    'Ledger ID': entry.ledgerId || '',
    'Clinic Name': vendor?.clinicName || '',
    'Doctor Name': vendor?.doctorName || vendor?.vendorName || '',
    'Entry Type': entry.entryType || '',
    'Service Type': entry.serviceType || '',
    Gross: num(entry.grossAmount),
    'List Price': breakdown.listPrice,
    'Customer Paid': breakdown.customerPaid,
    'Platform Offer': breakdown.offerDiscount,
    'Offer %': breakdown.offerDiscountRate,
    'Offer Absorbed': breakdown.offerAbsorbed,
    Refund: num(entry.refundAmount),
    'Base Commission': isDeliveryPartner ? 'NA' : breakdown.baseCommission,
    'Platform Commission': isDeliveryPartner ? 'NA' : breakdown.platformCommission,
    'GST on Commission': isDeliveryPartner ? 'NA' : num(entry.gstOnCommission),
    'Gateway Fee': num(entry.gatewayFeeRecovered),
    'Net Payable': num(entry.netPayable),
    'Settlement Status': String(entry.status || '').toUpperCase(),
    'Service Status': resolveServiceStatus(entry),
    'Settlement Eligible At': formatDateTime(entry.settlementEligibleAt),
    'Days Until Eligible':
      entry.daysUntilSettlementEligible != null ? Number(entry.daysUntilSettlementEligible) : '',
    'Created At': formatDateTime(entry.createdAt),
  };
};

const mapQueueRow = (entry: any, vendor: VendorSettlementExportVendor | null | undefined) => ({
  Date: formatDate(entry.serviceCompletedAt || entry.createdAt),
  'Service Ref': entry.serviceReferenceId || '',
  'Ledger ID': entry.ledgerId || '',
  'Clinic Name': vendor?.clinicName || '',
  'Doctor Name': vendor?.doctorName || vendor?.vendorName || '',
  'Entry Type': entry.entryType || '',
  'Net Payable': num(entry.netPayable),
  Status: String(entry.status || '').toUpperCase(),
  'Eligible At': formatDateTime(entry.settlementEligibleAt),
});

/**
 * Builds and downloads a multi-sheet Excel workbook for a vendor's settlement data.
 */
export function exportVendorSettlementExcel(input: ExportVendorSettlementExcelInput): {
  fileName: string;
  ledgerRows: number;
  queueRows: number;
} {
  const { vendor, summary, ledgerEntries, settlementQueue } = input;

  const settledAmount = ledgerEntries
    .filter((e) => String(e.status || '').toLowerCase() === 'settled')
    .reduce((sum, e) => sum + num(e.netPayable), 0);
  const totalRefunds = ledgerEntries.reduce((sum, e) => sum + num(e.refundAmount), 0);
  const totalGst = ledgerEntries.reduce((sum, e) => sum + num(e.gstOnCommission), 0);

  const overviewRows = [
    { Field: 'Vendor ID', Value: vendor?.vendorId || '' },
    { Field: 'Vendor Name', Value: vendor?.vendorName || '' },
    { Field: 'Clinic Name', Value: vendor?.clinicName || '' },
    { Field: 'Doctor Name', Value: vendor?.doctorName || '' },
    { Field: 'Vendor Type', Value: vendor?.vendorType || '' },
    { Field: 'Email', Value: vendor?.email || '' },
    { Field: 'Phone', Value: vendor?.phone || '' },
    { Field: 'Payout Policy', Value: summary.payoutPolicyLabel || 'T+2' },
    { Field: 'Exported At', Value: formatDateTime(new Date().toISOString()) },
    { Field: '', Value: '' },
    { Field: 'Total Net Payable', Value: num(summary.totalNetPayable) },
    { Field: 'Pending Settlement', Value: num(summary.pendingSettlement) },
    { Field: 'On Hold', Value: num(summary.onHold) },
    { Field: 'T+2 Settleable Amount', Value: num(summary.settleableAmount) },
    { Field: 'T+2 Settleable Count', Value: num(summary.settleableCount) },
    { Field: 'Waiting for T+2 Amount', Value: num(summary.waitingT2Amount) },
    { Field: 'Waiting for T+2 Count', Value: num(summary.waitingT2Count) },
    { Field: 'Settled Amount (loaded entries)', Value: settledAmount },
    { Field: 'Charges Recovered', Value: num(summary.totalCharges) },
    { Field: 'Platform Commission', Value: num(summary.totalPlatformCommission) },
    { Field: 'Base Commission', Value: num(summary.totalBaseCommission) },
    { Field: 'Offer Discount', Value: num(summary.totalOfferDiscount) },
    { Field: 'Offer Absorbed', Value: num(summary.totalOfferAbsorbed) },
    { Field: 'GST on Commission (loaded entries)', Value: totalGst },
    { Field: 'Total Refunds (loaded entries)', Value: totalRefunds },
    { Field: 'Ledger Entries Exported', Value: ledgerEntries.length },
    { Field: 'Settlement Queue Exported', Value: settlementQueue.length },
  ];

  const ledgerRows = ledgerEntries.map((entry) => mapLedgerRow(entry, vendor));
  const queueRows = settlementQueue.map((entry) => mapQueueRow(entry, vendor));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(overviewRows), 'Overview');
  XLSX.utils.book_append_sheet(
    workbook,
    XLSX.utils.json_to_sheet(
      ledgerRows.length
        ? ledgerRows
        : [{ Note: 'No ledger entries for the selected filters.' }]
    ),
    'Ledger'
  );
  XLSX.utils.book_append_sheet(
    workbook,
    XLSX.utils.json_to_sheet(
      queueRows.length
        ? queueRows
        : [{ Note: 'No T+2 eligible settlement queue entries.' }]
    ),
    'Settlement Queue'
  );

  const namePart = slugify(
    String(vendor?.doctorName || vendor?.clinicName || vendor?.vendorName || vendor?.vendorId || 'vendor')
  );
  const datePart = new Date().toISOString().slice(0, 10);
  const fileName = `vendor-settlement-${namePart}-${datePart}.xlsx`;

  XLSX.writeFile(workbook, fileName);

  return {
    fileName,
    ledgerRows: ledgerEntries.length,
    queueRows: settlementQueue.length,
  };
}
